import * as React from "react";
import { makeSaCapabilityAiClient } from "@eduviaos/shared";
import type { ApplyResult, CapabilityPromptContextWire, TargetKind } from "@eduviaos/shared";
import { useCapabilityAiAssistController } from "@eduviaos/capability-ai-ui";
import type { CapabilityAiAssist } from "@eduviaos/capability-ai-ui";
import { api } from "@/lib/api";

/**
 * Phase F-finale-4 Slice 6-FE — SA portal binding for the shared capability-AI
 * drawer controller.
 *
 * The state machine, prompt-context seam, and BE error → drawer-variant mapping
 * now live in `@eduviaos/capability-ai-ui` (`useCapabilityAiAssistController`)
 * so the institution-admin portal can reuse them (Phase F-finale-5 Slice 5b).
 * This module keeps only the SA-scoped client construction (the path slug is the
 * tenant authority) and re-exports the controller + error mapper so existing SA
 * import sites and unit tests stay on this path.
 */

export {
  useCapabilityAiAssistController,
  mapApiErrorToDrawerError,
} from "@eduviaos/capability-ai-ui";
export type {
  CapabilityAiAssist,
  CapabilityAiAssistControllerOptions,
  DrawerErrorInfo,
} from "@eduviaos/capability-ai-ui";

export interface UseSaCapabilityAiAssistOptions {
  tenantSlug: string;
  targetKind: TargetKind;
  buildContext: () => CapabilityPromptContextWire;
  onApplied?: (result: ApplyResult) => void;
}

/**
 * SA portal binding: builds the SA-scoped capability-AI client for the target
 * tenant (path slug is the scoping authority) and delegates to the controller.
 */
export function useSaCapabilityAiAssist({
  tenantSlug,
  targetKind,
  buildContext,
  onApplied,
}: UseSaCapabilityAiAssistOptions): CapabilityAiAssist {
  const client = React.useMemo(() => makeSaCapabilityAiClient(api, tenantSlug), [tenantSlug]);
  return useCapabilityAiAssistController({ client, targetKind, buildContext, onApplied });
}
